import { FreeRecommendation, LegacyFreePropertyMetrics } from "../types";
import { calculatePricingScore, calculateRankScore } from "./calculators";
import { RealtimeCompetitor, RealtimeProperty } from "@/features/realtimeProperty/types";

// Derive property metrics from property data that includes metrics
export function derivePropertyMetrics(
  property: RealtimeProperty,
  competitors: RealtimeCompetitor[]
): LegacyFreePropertyMetrics {
  // Get the latest metrics point for current values
  const latestMetrics = property?.data_points.length > 0
    ? property.data_points[property.data_points.length - 1]
    : null;

  if (!latestMetrics) {
    // Return default values if no metrics are available
    return {
      baseAdr: 0,
      reviewScore: property?.propertyFields.review_score || 0,
      reviewCount: property?.propertyFields.review_count || 0,
      isPreferredPartner: property?.propertyFields.is_preferred === true || property?.propertyFields.is_preferred_plus === true,
      visibilityBooster: property?.propertyFields.visibility_boosted ? 10 : 0,
      totalCompetitors: 0,
      ourRank: 0,
      medianCompetitorPrice: 0,
      medianCompetitorRank: 0,
      medianCompetitorReview: 0,
      hasPromotionalBadge: property?.propertyFields.badge_text?.length > 0 || false
    };
  }

  // Extract our price and competitor median price
  const ourPrice = latestMetrics.price.our;
  const medianPrice = latestMetrics.price.median;

  // Extract our rank and median competitor rank
  const ourRank = latestMetrics.rank.our;
  const medianRank = latestMetrics.rank.median;
  const totalProperties = latestMetrics.total_properties;

  // Extract review data from property information
  const reviewScore = property.propertyFields.review_score;
  const reviewCount = property.propertyFields.review_count;

  // Calculate median review score from competitors if available
  let medianCompetitorReview = 0;
  if (competitors && competitors.length > 0) {
    const competitorReviews = competitors
      .map(comp => comp.propertyFields.review_score)
      .filter(score => score !== undefined && score !== null);

    if (competitorReviews.length > 0) {
      // Sort and find median
      competitorReviews.sort((a, b) => a - b);
      const mid = Math.floor(competitorReviews.length / 2);
      medianCompetitorReview = competitorReviews.length % 2 === 0
        ? (competitorReviews[mid - 1] + competitorReviews[mid]) / 2
        : competitorReviews[mid];
    } else {
      // Fallback value if no competitor reviews are available
      medianCompetitorReview = 7.5;
    }
  } else {
    // Fallback value if no competitors are available
    medianCompetitorReview = 7.5;
  }

  // Get values from property object which now comes from API
  const isPreferredPartner = property.propertyFields.is_preferred === true || property.propertyFields.is_preferred_plus === true;
  const hasPromotionalBadge = property.propertyFields.badge_text?.length > 0 || false;
  const visibilityBooster = property.propertyFields.visibility_boosted ? 10 : 0;

  // Return a complete metrics object
  return {
    baseAdr: ourPrice,
    reviewScore,
    reviewCount,
    isPreferredPartner,
    visibilityBooster,
    totalCompetitors: totalProperties,
    ourRank,
    medianCompetitorPrice: medianPrice,
    medianCompetitorRank: medianRank,
    medianCompetitorReview,
    hasPromotionalBadge
  };
}

// Generate price optimization suggestion based on rank
export function getPriceOptimizationSuggestion(
  medianPrice: number,
  ourRank: number,
  medianRank: number
): { optimalPrice: number, pricingAdvice: string } {
  // Calculate optimal price based on rank position (adapted from colleague's code)
  let optimalPrice = medianPrice;
  let pricingAdvice = "";

  if (ourRank <= medianRank * 0.5) {
    // Top 50% of ranking - can command premium
    optimalPrice = medianPrice * 1.1;
    pricingAdvice = "Your excellent rank position allows for premium pricing";
  } else if (ourRank <= medianRank) {
    // Better than median rank - slight premium possible
    optimalPrice = medianPrice * 1.05;
    pricingAdvice = "Your good rank position allows for slightly above-market pricing";
  } else if (ourRank <= medianRank * 1.5) {
    // Worse than median but not terrible - slight discount needed
    optimalPrice = medianPrice * 0.95;
    pricingAdvice = "Your below-average rank position suggests a slight discount is appropriate";
  } else {
    // Much worse rank - significant discount needed
    optimalPrice = medianPrice * 0.9;
    pricingAdvice = "Your current rank position requires competitive pricing to attract bookings";
  }

  return { optimalPrice, pricingAdvice };
}


// Generate property insights based on metrics
export function generateFreeRecommendations(metrics: LegacyFreePropertyMetrics): FreeRecommendation[] {
  const insights: FreeRecommendation[] = [];
  // Get price optimization suggestion
  const priceSuggestion = getPriceOptimizationSuggestion(
    metrics.medianCompetitorPrice,
    metrics.ourRank,
    metrics.medianCompetitorRank
  );

  // Only generate insights if we have the required metrics
  if (!metrics.baseAdr || !metrics.medianCompetitorPrice) {
    // Price optimization suggestion (based on colleague's optimization calculations)
    const currentPriceDiff = ((metrics.baseAdr - priceSuggestion.optimalPrice) / priceSuggestion.optimalPrice) * 100;
    if (Math.abs(currentPriceDiff) > 5) {
      insights.push({
        message: `${priceSuggestion.pricingAdvice}. Consider ${currentPriceDiff > 0 ? 'reducing' : 'increasing'} your price from £${Math.round(metrics.baseAdr)} to approximately £${Math.round(priceSuggestion.optimalPrice)} to optimize for your current rank position.`,
        category: 'pricing',
        impactLevel: Math.abs(currentPriceDiff) > 15 ? 'high' : 'medium',
        actionType: 'price_optimization'
      });
    }

    // Quality-to-price position insight (based on colleague's price-quality analysis)
    if (insights.length > 0 && metrics.reviewScore && metrics.medianCompetitorReview) {
      const qualityAdvantage = metrics.reviewScore - metrics.medianCompetitorReview;
      const priceRatio = metrics.baseAdr / metrics.medianCompetitorPrice;

      if (qualityAdvantage > 0.5 && priceRatio < 0.9) {
        insights.push({
          message: `Your property has higher quality ratings but lower prices than competitors - excellent value proposition but potential for higher revenue with price increases.`,
          category: 'strategy',
          impactLevel: 'medium',
          actionType: 'price_quality_position'
        });
      } else if (qualityAdvantage < -0.5 && priceRatio > 1.1) {
        insights.push({
          message: `Your property has lower quality ratings but higher prices than competitors - likely to struggle with bookings. Consider improving quality or lowering prices.`,
          category: 'strategy',
          impactLevel: 'high',
          actionType: 'price_quality_position'
        });
      } else if (qualityAdvantage > 0.5 && priceRatio > 1.1) {
        insights.push({
          message: `Your premium pricing is justified by higher quality ratings - focus on communicating your unique value proposition in your listing.`,
          category: 'strategy',
          impactLevel: 'low',
          actionType: 'price_quality_position'
        });
      }
    }

    return insights;
  }

  // Calculate pricing score using the same logic as health score
  const pricingScore = calculatePricingScore(
    metrics.baseAdr,
    metrics.medianCompetitorPrice,
    metrics.ourRank,
    metrics.medianCompetitorRank
  );

  // Calculate rank score
  const rankScore = calculateRankScore(metrics.ourRank, metrics.totalCompetitors);

  // Calculate price position relative to median
  const priceDiff = ((metrics.baseAdr - metrics.medianCompetitorPrice) / metrics.medianCompetitorPrice) * 100;

  // Get price percentile description (from colleague's code)
  let pricePercentileText = "";
  const relativePriceRatio = metrics.baseAdr / metrics.medianCompetitorPrice;

  if (relativePriceRatio > 1.5) pricePercentileText = "Top 10% (very expensive)";
  else if (relativePriceRatio > 1.25) pricePercentileText = "Top 25% (expensive)";
  else if (relativePriceRatio > 1.1) pricePercentileText = "Above average price";
  else if (relativePriceRatio >= 0.9) pricePercentileText = "Average price range";
  else if (relativePriceRatio >= 0.75) pricePercentileText = "Below average price";
  else if (relativePriceRatio >= 0.6) pricePercentileText = "Bottom 25% (good value)";
  else pricePercentileText = "Bottom 10% (very cheap)";

  // Pricing position insight with price percentile
  if (pricingScore >= 70) {
    insights.push({
      message: `Your pricing strategy is optimal. Current price (£${Math.round(metrics.baseAdr)}) is well-positioned for your rank (#${metrics.ourRank}). ${pricePercentileText}.`,
      category: 'pricing',
      impactLevel: 'low',
      actionType: 'pricing_strategy'
    });
  } else if (pricingScore >= 50) {
    insights.push({
      message: `Your pricing could be optimized. Current price is in the ${pricePercentileText} category. Consider adjusting to better align with your rank (#${metrics.ourRank}).`,
      category: 'pricing',
      impactLevel: 'medium',
      actionType: 'pricing_strategy'
    });
  } else {
    insights.push({
      message: `Your pricing strategy needs attention. Current price (£${Math.round(metrics.baseAdr)}) is in the ${pricePercentileText} category and not optimal for your rank (#${metrics.ourRank}).`,
      category: 'pricing',
      impactLevel: 'high',
      actionType: 'pricing_strategy'
    });
  }

  // Price position insight (more specific)
  if (Math.abs(priceDiff) > 15) {
    if (priceDiff > 0) {
      insights.push({
        message: `Your price is ${Math.round(priceDiff)}% above market median. ${relativePriceRatio > 1.25 ? "This significant premium may be substantially reducing your visibility and bookings." : "This may be reducing your visibility and bookings."}`,
        category: 'pricing',
        impactLevel: relativePriceRatio > 1.25 ? 'high' : 'medium',
        actionType: 'price_position'
      });
    } else {
      insights.push({
        message: `Your price is ${Math.round(Math.abs(priceDiff))}% below market median. ${relativePriceRatio < 0.75 ? "While this improves occupancy, you may be significantly under-monetizing your property." : "Consider increasing rates to improve revenue."}`,
        category: 'pricing',
        impactLevel: relativePriceRatio < 0.75 ? 'high' : 'medium',
        actionType: 'price_position'
      });
    }
  }

  // Rank position insight
  if (rankScore >= 70) {
    insights.push({
      message: `Great rank position! You're in the top ${100 - Math.round(rankScore)}% of properties in your market.`,
      category: 'visibility',
      impactLevel: 'low',
      actionType: 'rank_optimization'
    });
  } else if (rankScore >= 40) {
    insights.push({
      message: `Your property rank (#${metrics.ourRank}) is moderate. Consider visibility enhancements to improve position.`,
      category: 'visibility',
      impactLevel: 'medium',
      actionType: 'rank_optimization'
    });
  } else {
    insights.push({
      message: `Your property rank (#${metrics.ourRank}) needs improvement. You're in the bottom ${100 - Math.round(rankScore)}% of properties.`,
      category: 'visibility',
      impactLevel: 'high',
      actionType: 'rank_optimization'
    });
  }

  // Promotional badge insight with more specific benefits (from colleague's code)
  if (!metrics.hasPromotionalBadge) {
    insights.push({
      message: `Adding a promotional badge could increase visibility by ~15%, improve search rank by ~15 positions, and boost booking potential by up to 5-8%.`,
      category: 'marketing',
      impactLevel: 'high',
      actionType: 'promotional_badge'
    });
  }

  // Preferred Partner status insight with more specific benefits
  if (!metrics.isPreferredPartner) {
    insights.push({
      message: `Preferred Partner status would improve your visibility by ~25% and allow you to maintain higher pricing with minimal impact on bookings.`,
      category: 'marketing',
      impactLevel: 'medium',
      actionType: 'preferred_partner'
    });
  }

  if (metrics.visibilityBooster === 0 && metrics.totalCompetitors > 0) {
    const rankPercentile = (metrics.totalCompetitors > 0) ? Math.round(metrics.totalCompetitors * 0.3) : 30;

    if (metrics.ourRank > rankPercentile) {
      const calculateVisibilityBoosterRankImpact = (boosterValue: number) => {
        if (boosterValue <= 0) return 0;
        // Use a more exponential formula to increase impact as booster value increases
        return Math.round(boosterValue * 1.2 * Math.pow(1.08, boosterValue));
      };

      const suggestedBoostLevel = metrics.ourRank > metrics.totalCompetitors * 0.6 ? 10 : 5;
      const estimatedRankImprovement = calculateVisibilityBoosterRankImpact(suggestedBoostLevel);
      const estimatedNewRank = Math.max(1, metrics.ourRank - estimatedRankImprovement);

      insights.push({
        message: `Consider adding Visibility Booster (${suggestedBoostLevel}%) to improve rank from #${metrics.ourRank} to approximately #${estimatedNewRank}, increasing visibility and booking potential.`,
        category: 'visibility',
        impactLevel: 'high',
        actionType: 'visibility_booster'
      });
    }
  }

  // Enhanced review score insight with more specific impact data
  if (metrics.reviewScore && metrics.medianCompetitorReview) {
    const reviewDiff = metrics.reviewScore - metrics.medianCompetitorReview;

    // More granular review status description (from colleague's code)
    let reviewStatus;
    let rankImpact;
    let conversionImpact;

    if (reviewDiff > 1.0) {
      reviewStatus = "Exceptional (top 5%)";
      rankImpact = 25;
      conversionImpact = 0.8;
    } else if (reviewDiff > 0.5) {
      reviewStatus = "Excellent (top 15%)";
      rankImpact = 15;
      conversionImpact = 0.5;
    } else if (reviewDiff > 0.2) {
      reviewStatus = "Above average";
      rankImpact = 8;
      conversionImpact = 0.3;
    } else if (reviewDiff >= -0.2) {
      reviewStatus = "Average";
      rankImpact = 0;
      conversionImpact = 0;
    } else if (reviewDiff >= -0.5) {
      reviewStatus = "Below average";
      rankImpact = -12;
      conversionImpact = -0.5;
    } else if (reviewDiff >= -1.0) {
      reviewStatus = "Poor (bottom 15%)";
      rankImpact = -22;
      conversionImpact = -0.8;
    } else {
      reviewStatus = "Very poor (bottom 5%)";
      rankImpact = -35;
      conversionImpact = -1.2;
    }

    if (reviewDiff < -0.5) {
      insights.push({
        message: `Your review score (${metrics.reviewScore}) is rated as "${reviewStatus}" compared to competitors (${metrics.medianCompetitorReview.toFixed(1)}). This negatively impacts your rank by ~${Math.abs(rankImpact)} positions and reduces booking conversion by ~${Math.abs(conversionImpact)}%. Focus on improving guest experience.`,
        category: 'marketing',
        impactLevel: 'high',
        actionType: 'review_improvement'
      });
    } else if (reviewDiff < 0) {
      insights.push({
        message: `Your review score (${metrics.reviewScore}) is ${reviewStatus} compared to competitors (${metrics.medianCompetitorReview.toFixed(1)}). This impacts your rank by ~${Math.abs(rankImpact)} positions. Minor improvements could help.`,
        category: 'marketing',
        impactLevel: 'medium',
        actionType: 'review_improvement'
      });
    } else if (reviewDiff > 0.2) {
      insights.push({
        message: `Your review score (${metrics.reviewScore}) is ${reviewStatus}, above the competitor average (${metrics.medianCompetitorReview.toFixed(1)}). This boosts your rank by ~${rankImpact} positions and improves conversion by ~${conversionImpact}%. Consider highlighting this advantage in your listing.`,
        category: 'marketing',
        impactLevel: 'low',
        actionType: 'review_highlight'
      });
    }
  }

  // Enhanced review count insight with listing maturity concept
  if (metrics.reviewCount === 0) {
    insights.push({
      message: `Your property has no reviews. New listings with no reviews typically see significantly reduced visibility and conversion rates. Prioritize getting your first reviews.`,
      category: 'marketing',
      impactLevel: 'high',
      actionType: 'review_count'
    });
  } else if (metrics.reviewCount < 4) {
    insights.push({
      message: `Your property has only ${metrics.reviewCount} review${metrics.reviewCount > 1 ? 's' : ''}. Very new listings (1-3 reviews) have approximately 15-20% reduced visibility. Encourage more guests to leave reviews.`,
      category: 'marketing',
      impactLevel: 'high',
      actionType: 'review_count'
    });
  } else if (metrics.reviewCount < 10) {
    insights.push({
      message: `Your property has ${metrics.reviewCount} reviews. New listings (4-9 reviews) have ~5-10% reduced visibility. Continuing to build reviews will improve credibility and ranking.`,
      category: 'marketing',
      impactLevel: 'high',
      actionType: 'review_count'
    });
  } else if (metrics.reviewCount < 30) {
    insights.push({
      message: `Your property has ${metrics.reviewCount} reviews. Growing listings (10-29 reviews) have moderate credibility. Increasing beyond 30 reviews would further boost visibility by ~5-10%.`,
      category: 'marketing',
      impactLevel: 'medium',
      actionType: 'review_count'
    });
  } else if (metrics.reviewCount < 100) {
    insights.push({
      message: `Your property has ${metrics.reviewCount} reviews. Established listings (30-99 reviews) have good credibility. Reaching 100+ reviews would provide an additional visibility boost of ~5%.`,
      category: 'marketing',
      impactLevel: 'low',
      actionType: 'review_count'
    });
  } else {
    insights.push({
      message: `Your property has ${metrics.reviewCount} reviews. Mature listings (100+ reviews) have maximum credibility and receive a significant visibility bonus. Maintain your excellent review collection strategy.`,
      category: 'marketing',
      impactLevel: 'low',
      actionType: 'review_count'
    });
  }

  return insights;
}